import { useCallback, useRef, useState } from 'react';

type EqualFunc<T> = (a: T, b: T) => boolean;

const defaultEqual = <T>(a: T, b: T) => a === b;

export const useOptimalState = <T>(
    initialValue: T | (() => T),
    eqFunc: EqualFunc<T> = defaultEqual
): [T, (value: T) => void] => {
    const wasInitedRef = useRef(false);

    let init: T | undefined;
    if (!wasInitedRef.current) {
        wasInitedRef.current = true;

        init = initialValue instanceof Function ? initialValue() : initialValue;
    }

    const [state, setState] = useState(initialValue);
    const valueRef = useRef(init);

    const setStateIfDifferent = useCallback(
        (value: T) => {
            if (!eqFunc(value, valueRef.current as T)) {
                valueRef.current = value;
                setState(value);
            }
        },
        [eqFunc]
    );

    return [state, setStateIfDifferent];
};
